<template>
  <div class="mb-4">
    <template v-if="expand">
      <v-btn icon small class="mt-n1" @click="showInitialContents=!showInitialContents"><v-icon>mdi-menu-down</v-icon></v-btn>Initial contents
    </template>
    <v-slide-y-transition>
      <outlined-block :class="{'ml-4':expand}" v-show="showInitialContents">
        <v-container class="pb-0">

          <v-row dense>
            <v-col v-if="!spiritsOnly" cols="7" md="4">
              <v-select :items="['Spirit','Wine','Non-excise']" v-model="contents" label="Type" outlined dense hide-details @change="toggleType()"/>
            </v-col>
            <v-col cols="12" md="8">
              <v-autocomplete v-model="initial.alcoholId" outlined dense label="Initial contents" hide-details
                              :items="alcoholItems" item-text="name" item-value="id"
                              no-data-text="None available">
                <template v-slot:prepend-item>
                  <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="createNewAlcohol()">
                    <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new {{contents}}
                  </div>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-row dense class="mt-6">
            <v-col cols="8" md="5">
              <date-picker :label="dateLabel" v-model="initial.date" :disabled="initial.alcoholId==''"/>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="6" md="3">
              <decimal-number-input label="Volume" v-model="initial.volume" prefix="" :disabled="initial.alcoholId==''"
                                    :suffix="contents==='Spirit'?'LAA':'litres'" :precision="3"/>
            </v-col>
            <v-col cols="6" md="3" v-show="contents==='Spirit'" v-if="trackLitres">
              <decimal-number-input label="Volume" v-model="initial.litres" prefix="" :disabled="initial.alcoholId==''"
                                    suffix="litres" :precision="3"/>
            </v-col>
            <v-col cols="6" md="3" v-show="contents==='Spirit'" v-if="trackKg">
              <decimal-number-input label="Weight" v-model="initial.kg" prefix="" :disabled="initial.alcoholId==''"
                                    suffix="kg" :precision="3"/>
            </v-col>
            <v-col cols="6" md="3" v-show="contents==='Spirit' || contents ==='Wine'" v-if="trackStrength || contents === 'Wine'">
              <decimal-number-input label="Strength" v-model="initial.strength" prefix="" :disabled="initial.alcoholId==''"
                                    suffix="% alc./vol." :precision="2"/>
            </v-col>
            <v-col cols="6" md="3">
              <decimal-number-input label="Value" v-model="initial.value" :disabled="initial.alcoholId==''"/>
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field outlined dense label="Lot" v-model="initial.lot" :rules="[lotRequired,fieldRules.maxLength(20)]" :disabled="initial.alcoholId==''"/>
            </v-col>
          </v-row>
        </v-container>
      </outlined-block>
    </v-slide-y-transition>

    <spirit-quick-create v-model="spiritCreate" @create="spiritCreated"/>
    <wine-quick-create v-model="wineCreate" @create="wineCreated"/>
    <alcohol-non-excise-quick-create v-model="nonExciseCreate" @create="nonExciseCreated"/>
  </div>
</template>

<script>
import SpiritQuickCreate from '@/views/alcohol/spirits/SpiritQuickCreate'
import WineQuickCreate from '@/views/alcohol/wine/WineQuickCreate'
import AlcoholNonExciseQuickCreate from '@/views/alcohol/nonExcise/AlcoholNonExciseQuickCreate'

export default {
  name: 'ExciseVesselInitial',
  components: {SpiritQuickCreate,WineQuickCreate,AlcoholNonExciseQuickCreate},
  props: {
    initial: {type:Object, required:true},
    dateLabel: {type:String, required:false, default:'As of'},
    spiritsOnly: {type:Boolean, required:false, default:false},
    expand: {type:Boolean, required:false, default:true}
  },
  data () {
    return {
      showInitialContents: !this.expand,
      spirits: [],
      wines: [],
      nonExcises: [],
      contents: 'Spirit',
      spiritLot: '',
      wineLot: '',
      nonExciseLot: '',
      lotRequired: v => !this.initial.volume || !!v || 'Required',
      spiritCreate:false,
      wineCreate:false,
      nonExciseCreate: false
    }
  },
  computed: {
    alcoholItems() {
      return this.contents === 'Spirit' ? this.spirits : (this.contents === 'Wine' ? this.wines : this.nonExcises);
    },
    trackLitres() {
      return this.$store.getters.company.settings.trackLitres;
    },
    trackKg() {
      return this.$store.getters.company.settings.trackKg;
    },
    trackStrength() {
      return this.$store.getters.company.settings.trackStrength;
    }
  },
  async created() {
    await Promise.all([
      await this.$lazyListLoad(this.spirits, 'spirits2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]),
      this.$store.getters.company.allowWine ? await this.$lazyListLoad(this.wines, 'wines2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]) : [],
      await this.$lazyListLoad(this.nonExcises, 'nonExcises2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}])
    ]);
    const results = await this.$apiCall(`query ExciseVesselInitial {
                                              spiritLot: lotUnique(prefix:"S")
                                              wineLot: lotUnique(prefix:"W")
                                              nonExciseLot: lotUnique(prefix:"N")
                                          }`,
                                        {});
    if (!results.error) {
      if (this.spirits.length || this.wines.length || this.nonExcises.length) {
        this.spiritLot = results.data.spiritLot;
        this.wineLot = results.data.wineLot;
        this.nonExciseLot = results.data.nonExciseLot;
        this.toggleType();
      }
    }
  },
  methods: {
    createNewAlcohol() {
      if (this.contents === 'Spirit')
        this.spiritCreate = true;
      else if (this.contents === 'Wine')
        this.wineCreate = true;
      else
        this.nonExciseCreate = true;
    },
    spiritCreated(spirit) {
      this.spirits.push(spirit);
      this.initial.alcoholId = spirit.id;
    },
    wineCreated(wine) {
      this.wines.push(wine);
      this.initial.alcoholId = wine.id;
    },
    nonExciseCreated(nonExcise) {
      this.nonExcises.push(nonExcise);
      this.initial.alcoholId = nonExcise.id;
    },
    toggleType() {
      this.initial.alcoholId = '';
      this.initial.lot = this.contents === 'Spirit' ? this.spiritLot : (this.contents === 'Wine' ? this.wineLot : this.nonExciseLot);
    }
  }
}
</script>